import * as angular from 'angular';

export interface ProductSearchMatch {
  id: number;
  description: string;
  catalogNumber: string;
  number: string;
  minPrice: number;
  maxPrice: number;
  packageName: string;
  unitsInPackage: number;
}

export interface CategoriesResponse {
  ids: Category[];
}

export interface Category {
  id: number;
  name: string;
}

export interface ProductsResponse {
  category: {
    id: number;
    name: string;
  };
  unspscs: Unspsc[];
  notificationThreshold: number;
}

export interface Unspsc {
  unspsc: {
    id: number;
    name: string;
  };
  products: Product[];
}

export interface Product {
  product: {
    id: number;
    name: string;
  };
  catalogNumber: any;
  seller: {
    id: number;
    name: string;
  };
  minPrice: number;
  maxPrice: number;
  selectedPkgType: string;
  selectedUnitInPackage: number;
  canEditPricing: boolean;
  packages: ProductPackage[];
  prodPackConversions: any;
  pending: any;
  historicalMinPrice: {
    pricePerUnit: number;
    buyer: {
      id: number;
      name: string;
    };
  };
  historicalMaxPrice: {
    pricePerUnit: number;
    buyer: {
      id: number;
      name: string;
    };
  };
  lastUpdated: number;
  isOnCA: boolean;
  unspsc?: any;
  category?: any;
  uom?: string;
  unitsInPackage?: number;
  unitCount?: number;
  spend?: number;
  displayUom?: string;
  formularyPricing?: number;
}

export interface ProductPackage {
  productPkgId: number;
  gtin: any;
  packageType: string;
  units: number;
}

export interface ProductToEdit {
  productId: number;
  minPrice: number;
  maxPrice: number;
  selectedPkgType: string;
  packages: ProductServicePackage[];
  prodPackConversions: ProductPackageConversion[];
}

interface ProductPackageConversion {
  packConvId: number;
  fromPkg: ProductServicePackage;
  toPkg: ProductServicePackage;
  factor: number;
}

interface ProductServicePackage {
  productPkgId: number;
  gtin: any;
  packageType: string;
}

export default class ProductsService {
  baseUrl = '/aptitude/api/products/';

  constructor(private $http, private $q) {
    'ngInject';
  }

  searchProductsInUnspsc(
    unspscId: number,
    search: string,
    productNumber: number,
    pageSize: number,
    options: any,
    categoryId?: number
  ): ng.IPromise<ProductSearchMatch[]> {
    // Currently limited to searching by manufacturer product number and description
    // TODO: talk to mark about using an ordered-sequence number instead of a productId as start-from index
    // for progressive loading - would be handy to also get a total results value

    /**
     * @params
     * @unspsc: unspsc Id
     * @search: Searches on product number and description
     * @pageFromProductId: OPTIONAL: acts as start-from index
     * @pageSize: OPTIONAL: default 25 - max 100
     */

    return this.$http.get(this.baseUrl, {
      cache: true,
      params: {
        // if categoryId is provided then omit UNSPSC ID
        unspsc: categoryId ? null : unspscId,
        categoryId,
        search: search,
        pageFromProductNumber: productNumber || null,
        pageSize: pageSize || null
      }
    });
  }

  getCategories(sellerId): ng.IPromise<CategoriesResponse> {
    return this.$http.get(
      '/aptitude/SellerManageProductsServiceController/getCategories',
      {
        intercept: true,
        params: {
          sellerId: sellerId
        }
      }
    );
  }

  getProductsByCategory(categoryId, sellerId): ng.IPromise<ProductsResponse> {
    return this.$http.get(
      '/aptitude/SellerManageProductsServiceController/getProducts',
      {
        intercept: true,
        params: {
          sellerId: sellerId,
          categoryId: categoryId
        }
      }
    );
  }

  /**
   * Get product details
   *
   * @param  {number} productId The product id
   * @return A ProductsResponse object containing only one product.
   */
  getProduct(
    productId: number,
    sellerId: number
  ): ng.IPromise<ProductsResponse> {
    // Make a call to get the desired product. We can't return the promise from this call directly because we want
    // to strip away some of the JSON we'll get back and return just a subset of it. That's why we created another
    // promise above.
    return this.$http.get(
      '/aptitude/SellerManageProductsServiceController/getProduct',
      {
        intercept: true,
        params: {
          sellerId: sellerId,
          productId: productId
        }
      }
    );
  }

  /**
   * Perform immediate updates to pricing, packaging, and conversions. Other edits (for example to catalog number
   * and description are done via request to edit).
   *
   * @param editProductRequest
   * @return A promise which will be resolved upon success with the updated version of the product (which should have all the latest package counts, prices, etc.).
   */
  editProductOnUnspsc(editProductRequest: ProductToEdit) {
    return this.$http.post(
      '/aptitude/SellerManageProductsServiceController/editProductOnUnspsc',
      editProductRequest
    );
  }

  // TODO: FIXME: This should not be a get!
  removeProduct(productId: number, removalReason: string, comments: string) {
    return this.$http.get(
      '/aptitude/SellerManageProductsServiceController/removeProduct',
      {
        intercept: true,
        params: {
          productId: productId,
          removalReason: removalReason,
          comments: comments
        }
      }
    );
  }

  /** OM-739 Change product status
   * URL api/products/{productId}/status
   *
   * Request { status:"Active"}
   */
  changeProductStatus(productId: number, status: string) {
    return this.$http.put('/aptitude/api/products/' + productId + '/status', {
      productId: productId,
      status: status
    });
  }

  getProductCrosses(productId: number) {
    return this.$http.get(
      `/aptitude/api/products/${productId}/crossreferences`
    );
  }

  getProductFromCrossReferences(productId: number) {
    return this.$http.get(
      `/aptitude/api/products/${productId}?fields=crossReference`
    );
  }

  // Get search result products
  getSearchProducts(searchValue) {
    let canceller = this.$q.defer();
    let cancel = (reason) => {
      canceller.resolve(reason);
    };

    let promise = this.$http
      .get(
        `/aptitude/api/products/_search?searchValue=${searchValue}&aggregateOnly=false`,
        { timeout: canceller.promise }
      )
      .then((response) => {
        return response;
      });

    return {
      promise: promise,
      cancel: cancel
    };
  }
}
